import { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { RefreshCw, Upload } from 'lucide-react';

export default function LeadsAdminTab({ caps, isPreviewEnv }) {
  const [batches, setBatches] = useState([]);
  const [fromUserId, setFromUserId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [keepId, setKeepId] = useState('');
  const [mergeIds, setMergeIds] = useState('');
  const [feedItems, setFeedItems] = useState([]);
  const [pullLoading, setPullLoading] = useState(false);
  const [zohoFile, setZohoFile] = useState(null);
  const [zohoNotesFile, setZohoNotesFile] = useState(null);
  const [zohoFileInputKey, setZohoFileInputKey] = useState(0);
  const [zohoDryRun, setZohoDryRun] = useState(true);
  const [zohoJobId, setZohoJobId] = useState(null);
  const [zohoJob, setZohoJob] = useState(null);
  const [zohoStarting, setZohoStarting] = useState(false);
  const [importJobs, setImportJobs] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await leadsAPI.listBatches();
      setBatches(data);
    } catch (e) { /* ignore */ }
    try {
      const { data } = await leadsAPI.listFeed();
      setFeedItems(data);
    } catch (e) { /* ignore */ }
    try {
      const { data } = await leadsAPI.listZohoImportJobs();
      setImportJobs(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!zohoJobId) return;
    const poll = setInterval(async () => {
      try {
        const { data } = await leadsAPI.getZohoImportJob(zohoJobId);
        setZohoJob(data);
        if (data.status !== 'RUNNING') {
          clearInterval(poll);
          load();
        }
      } catch (e) { clearInterval(poll); }
    }, 1500);
    return () => clearInterval(poll);
  }, [zohoJobId, load]);

  const doReassign = async () => {
    try {
      const { data } = await leadsAPI.reassignAll({ from_user_id: fromUserId, to_user_id: toUserId });
      toast.success(`Reassigned ${data.reassigned} lead(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reassign failed'); }
  };

  const doMerge = async () => {
    try {
      const ids = mergeIds.split(',').map(s => s.trim()).filter(Boolean);
      const { data } = await leadsAPI.merge({ keep_id: keepId, merge_ids: ids });
      toast.success(`Merged ${data.merged.length} lead(s) into ${keepId}, moved ${data.activities_moved} activities`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
  };

  const doPullNow = async () => {
    setPullLoading(true);
    try {
      const { data } = await leadsAPI.feedPullNow();
      toast.success(data.status ? data.status : `Fetched ${data.fetched}, ${data.new_found} new lead(s) logged`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Pull failed'); }
    finally { setPullLoading(false); }
  };

  const doZohoStart = async () => {
    if (!zohoFile) { toast.error('Choose the Zoho Leads export CSV first'); return; }
    setZohoStarting(true);
    try {
      const { data } = await leadsAPI.zohoImportStart(zohoFile, zohoNotesFile, zohoDryRun);
      toast.success(zohoDryRun ? 'Dry run started' : 'Import started');
      setZohoJobId(data.job_id);
      setZohoJob({ status: 'RUNNING', dry_run: zohoDryRun });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed to start'); }
    finally { setZohoStarting(false); }
  };

  const resetZohoForm = () => {
    setZohoFile(null); setZohoNotesFile(null); setZohoFileInputKey(k => k + 1);
    setZohoJobId(null); setZohoJob(null); setZohoDryRun(true);
  };

  return (
    <div className="space-y-4 mt-4" data-testid="leads-admin-tab">
      <Card className="p-4 space-y-3 max-w-xl">
        <h3 className="font-semibold">Reassign all leads</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From user id</Label><Input value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} data-testid="leads-reassign-from-input" /></div>
          <div><Label>To user id</Label><Input value={toUserId} onChange={(e) => setToUserId(e.target.value)} data-testid="leads-reassign-to-input" /></div>
        </div>
        <Button onClick={doReassign} data-testid="leads-reassign-submit-btn">Reassign all open leads</Button>
      </Card>

      <Card className="p-4 space-y-3 max-w-xl">
        <h3 className="font-semibold">Merge duplicate leads</h3>
        <div><Label>Keep lead id</Label><Input value={keepId} onChange={(e) => setKeepId(e.target.value)} data-testid="leads-merge-keep-input" /></div>
        <div><Label>Merge lead id(s), comma-separated</Label><Input value={mergeIds} onChange={(e) => setMergeIds(e.target.value)} data-testid="leads-merge-ids-input" /></div>
        <Button onClick={doMerge} data-testid="leads-merge-submit-btn">Merge</Button>
      </Card>

      <Card className="p-4" data-testid="leads-batches-card">
        <h3 className="font-semibold mb-2">Upload batches</h3>
        {batches.length === 0 ? <p className="text-sm text-muted-foreground">No batches yet</p> : (
          <div className="divide-y">
            {batches.map(b => (
              <div key={b.id} className="py-2 text-sm flex items-center justify-between" data-testid={`leads-batch-${b.id}`}>
                <span>{b.name} — created {b.rows_created}, flagged kept {b.rows_flagged_kept}, rejected {b.rows_rejected}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3" data-testid="leads-feed-card">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Zoho CRM leads feed (read-only)</h3>
          {caps?.is_super_admin && (
            <Button size="sm" variant="outline" onClick={doPullNow} disabled={pullLoading || isPreviewEnv} title={isPreviewEnv ? 'Production only — Preview stays read-only and local' : ''} data-testid="leads-feed-pull-now-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Pull now
            </Button>
          )}
        </div>
        {feedItems.length === 0 ? <p className="text-sm text-muted-foreground">No feed entries yet</p> : (
          <div className="max-h-56 overflow-y-auto divide-y">
            {feedItems.map(f => (
              <div key={f.id} className="py-2 text-sm flex items-center justify-between" data-testid={`leads-feed-row-${f.id}`}>
                <span className="truncate">{f.company || f.zoho_lead_id}</span>
                <Badge variant="outline">{f.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {caps?.is_super_admin && (
      <Card className="p-4 space-y-3 max-w-xl" data-testid="leads-zoho-import-card">
        <h3 className="font-semibold">Import leads from Zoho CRM (Super Admin only)</h3>
        <p className="text-xs text-muted-foreground">Needs the leads CSV from the Zoho export; the notes CSV is optional. Dry run is checked by default — nothing is created until you uncheck it.</p>
        <div><Label>Leads CSV</Label><Input type="file" key={`leads-${zohoFileInputKey}`} accept=".csv" onChange={(e) => setZohoFile(e.target.files[0])} data-testid="leads-zoho-import-file-input" /></div>
        <div><Label>Notes CSV (optional)</Label><Input type="file" key={`notes-${zohoFileInputKey}`} accept=".csv" onChange={(e) => setZohoNotesFile(e.target.files[0])} data-testid="leads-zoho-import-notes-file-input" /></div>
        <div className="flex items-center gap-2">
          <Checkbox id="zoho-dry-run" checked={zohoDryRun} onCheckedChange={setZohoDryRun} data-testid="leads-zoho-import-dry-run-checkbox" />
          <Label htmlFor="zoho-dry-run">Dry run (preview only, nothing is created)</Label>
        </div>
        <Button onClick={doZohoStart} disabled={zohoStarting || (zohoJob && zohoJob.status === 'RUNNING')} data-testid="leads-zoho-import-start-btn">
          <Upload className="h-4 w-4 mr-1" /> {zohoDryRun ? 'Run dry run' : 'Start import'}
        </Button>

        {zohoJob && (
          <div className="space-y-2 border-t pt-3" data-testid="leads-zoho-import-progress">
            <div className="flex items-center justify-between text-sm">
              <span>Status: <Badge variant="outline">{zohoJob.status}</Badge> {zohoJob.dry_run ? '(dry run)' : ''}</span>
              {zohoJob.total_rows > 0 && <span className="text-muted-foreground">{zohoJob.processed_rows || 0}/{zohoJob.total_rows} rows</span>}
            </div>
            {zohoJob.status === 'ERROR' && <p className="text-sm text-destructive" data-testid="leads-zoho-import-error">{zohoJob.error}</p>}
            {zohoJob.status === 'DONE' && zohoJob.report && (
              <div className="space-y-2 text-sm" data-testid="leads-zoho-import-report">
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(zohoJob.report.counts || {}).map(([k, v]) => <Badge key={k} variant="outline" data-testid={`leads-zoho-import-count-${k}`}>{k}: {v}</Badge>)}
                  <Badge variant="outline">notes activities: {zohoJob.report.notes_activities_created || 0}</Badge>
                </div>
                {(zohoJob.report.issues || []).length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded-sm p-2 text-xs space-y-1">
                    {zohoJob.report.issues.map((issue, i) => <div key={i}>{issue}</div>)}
                  </div>
                )}
                {(zohoJob.report.owner_unmatched || []).length > 0 && (
                  <div className="max-h-28 overflow-y-auto border rounded-sm p-2 text-xs space-y-1 text-amber-700">
                    {zohoJob.report.owner_unmatched.map((o, i) => <div key={i}>{o}</div>)}
                  </div>
                )}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={resetZohoForm} data-testid="leads-zoho-import-reset-btn">Start a new import</Button>
          </div>
        )}

        {importJobs.length > 0 && (
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold mb-2">Past import jobs</h4>
            <div className="divide-y max-h-56 overflow-y-auto">
              {importJobs.map(j => (
                <div key={j.id} className="py-2 text-sm flex items-center justify-between" data-testid={`leads-zoho-import-job-${j.id}`}>
                  <span className="truncate">{j.leads_filename}</span>
                  <span className="text-muted-foreground">{j.status} {j.dry_run ? '(dry run)' : ''} · {j.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}

